import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { ExtendedPropertiesType } from "UI/types";
import { Event } from "../../../../Global/Base/Events";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { Enum } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IValidatable, IValidatableContainer, IValidationResult } from "../../../UI.BaseControls/ControlInterfaces";
import { ICompactDataGridAttrInfo } from "../../CompactDatagrid";
import { IDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { TableCell } from "./TableCell";

export const DOUBLE_CLICK_TIME = 300;

export interface PreviewRowClickEvent extends CustomEvent {
    /** Původní even kliknutí myší, který tuto událost vyvolal */
    originalEvent: MouseEvent;
    /** Řádek, který tuto událost vyvolal */
    row: IDataRowElement;
}
export interface RowClickEvent extends CustomEvent {
    /** Původní even kliknutí myší, který tuto událost vyvolal */
    originalEvent: MouseEvent;
    /** Řádek, který tuto událost vyvolal */
    row: IDataRowElement;
    /** Počet kliknutí na řádek */
    clickCount: number;
}

/**
 * Reprezentuje datový záznam bez vizuální reprezentace
 * @includeToDoc
 */
export interface IDataRow extends IDisposable {
    /**
     * Událost vyvolávaná při změně vybrání záznamu
     */
    readonly selectionStateChanged: Event<IDataRow, boolean>;

    /**
     * Hodnota identifikátoru dat
     */
    dataKey: string;
    /**
     * Zda jsou data vybrána
     */
    isSelected: boolean;
    /**
     * Zda jsou data smazána (např. jiným uživatelem)
     */
    isDeleted: boolean;

    /**
     * Vrací true, pokud je řádek disposed.
     *
     * TODO - domyslet a přesunout do jiného rozhraní v rámci https://tfs.tescosw.loc/Jadro/Jadro%20.NET/_workitems/edit/31564
     */
    isDisposed?(): boolean;

    /**
     * Vrací dodatečné vlastnosti daného atributu
     * @param attrName Jméno atributu, jehož vlastnosti se vracejí
     * @returns Dodatečné vlastnosti
     */
    getExtendedProperties?(attrName: string): ExtendedPropertiesType;
}

export abstract class IDataRow {
    private static _properties: (keyof IDataRow)[] = ["selectionStateChanged", "dataKey", "isSelected", "isDeleted"];
    private static _methods: (keyof IDataRow)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Reprezentuje *element* představující datový záznam *nezávislý na konečné vizuální podobě*
 * @includeToDoc
 */
export interface IDataRowElement extends IDataRow, HTMLElement, IValidatableContainer {
    /**
     * Událost vyvolávaná při změně validnosti buňky záznamu
     */
    readonly cellValidityChanged: Event<IValidatable, IValidationResult>;

    /**
     * Zda je element připojený v DOMu
     */
    readonly isAttached: boolean;
    /**
     * Element buňky s ovládacími prvky záznamu
     */
    readonly controlsCellElement?: HTMLElement;
    /**
     * Buňky zobrazující jednotlivé atributy datového záznamu
     */
    readonly dataCells: readonly TableCell[];
    /**
     * Datagrid, ve kterém je záznam zobrazen
     */
    readonly dataGrid: IDataGrid<IDataRowElement>;
    /**
     * Zda je element viditelný
     */
    visible: boolean;
    /**
     * Flag označující záznam (například pro stylování)
     */
    flag?: Enum | string;

    /**
     * Zinicializuje datový element
     * @param rowData Data k zobrazení
     * @param ownerGrid Rodičovský datagrid
     * @param controlElement Element s ovládacími prvky záznamu
     */
    init(rowData: IRowData, ownerGrid: IDataGrid<IDataRowElement>, controlElement: HTMLElement): void;
    /**
     * Vykreslí datový element
     */
    render(): HTMLElement;
    /**
     * Nastaví data k zobrazení
     * @param attrName Název atributu, pro nějž se nastavují data
     * @param value Nová data
     */
    setData(attrName: string, value: IAttrValue): Promise<void>;
    /**
     * Vybere řádek bez ohledu na stav seznamu (označí jako vybraný)
     * @param isFirstSelection Zda se jedná o úplně první výber nově vytvořeného řádku
     */
    select(isFirstSelection?: boolean): void;
    /**
     * Vybere záznam v případě, že lze tuto akci provést (např. není rozeditovaný jiný seznam)
     * @param withFocus Zda má být řádek vybrán s nastavením focusu na seznam. Využívá se při výběru řádku během otevírání menu
     */
    selectWithCheckChanges(withFocus: boolean): Promise<void>;

    /**
     * Přidá a vyrenderuje sloupce na konec řádku.
     * @param columns sloupce k přidání
     */
    addCells(columns: Column[]): void;
}

export abstract class IDataRowElement {
    private static _properties: (keyof IDataRowElement)[] = ["cellValidityChanged", "dataCells", "dataGrid", "visible", "flag"];
    private static _methods: (keyof IDataRowElement)[] = ["init", "render", "setData", "select", "selectWithCheckChanges"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IDataRow && instance instanceof HTMLElement && instance instanceof IValidatableContainer;
    }
}

/**
 * Reprezentuje element představující datový záznam *nezávislý na konečné vizuální podobě*, který umožňuje editaci zobrazených hodnot
 * @includeToDoc
 */
export interface IEditableDataRowElement extends IDataRowElement {
    /**
     * Zda je řádek v režimu editace
     */
    isInEdit: boolean;
    /**
     * Zda je zobrazovaný záznam změněný (zeditovaný)
     */
    isChanged: boolean;

    /**
     * Nastaví nová (změněná) data a označí řádek jako změněný
     * @param attrName Název atributu, jehož data se mění
     * @param value Nová hodnota dat
     * @param isChange Zda má být řádek změněný
     */
    setChangedData(attrName: string, value: IAttrValue, isChange: boolean): void;
    /**
     * Nastavení novou instanci pro buňky v editaci (LOVEdit a FileEdit)
     * @param obj - nově načtený object pro řádek
     */
    setInstance(obj: OWObject): void;
}

export abstract class IEditableDataRowElement {
    private static _properties: (keyof IEditableDataRowElement)[] = ["isInEdit", "isChanged"];
    private static _methods: (keyof IEditableDataRowElement)[] = ["setChangedData", "setInstance"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function")
            && instance instanceof IDataRowElement;
    }
}

/**
 * Element reprezentující *seznamový řádek* gridu, tedy jeden řádek klasické tabulky - buňka pro každý sloupec
 * @includeToDoc
 */
export interface IListDataRowElement extends IDataRowElement {
    /**
     * Přesune buňku na jinou pozici
     * @param fullAttrName Název atributu, který buňka zobrazuje
     * @param newPosition Nová pozice, na které má být buňka umístěna
     */
    changeCellPositionByFullAttrName(fullAttrName: string, newPosition: number): void;
    /**
     * Přesune buňku na jinou pozici u sloupců bez atributu
     * @param index Index, na kterém se buňka nachází
     * @param newPosition Nová pozice, na které má být buňka umístěna
     */
    changeCellPositionByIndex(index: number, newPosition: number): void;
    /**
     * Změní viditelnost buňky
     * @param attrName Název atributu, který buňka zobrazuje
     * @param visible Zda má být buňka viditelná nebo ne
     */
    toggleVisibilityCellByAttrName(attrName: string, visible: boolean): void;
    /**
     * Změní šířku buňky
     * @param attrName Název atributu, který buňka zobrazuje
     * @param width Nová šířka buňky
     */
    resizeCellByAttrName(attrName: string, width: number): void;
    resizeCellByFullAttrName(attrName: string, width: number): void;
    /**
     * Změní šířku buňky pro sloupce bez atributu
     * @param index Index, na kterém se buňka nachází
     * @param width Nová šířka buňky
     */
    resizeCellByIndex(index: number, width: number): void;
    /**
     * Odstraní buňku
     * @param index Pozice buňky, která se má odstranit
     */
    removeCellByIndex(index: number): void;
}

export abstract class IListDataRowElement {
    private static _properties: (keyof IListDataRowElement)[] = [];
    private static _methods: (keyof IListDataRowElement)[] = ["changeCellPositionByFullAttrName", "toggleVisibilityCellByAttrName", "resizeCellByAttrName", "removeCellByIndex"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IDataRowElement;
    }
}

/**
 * Element reprezentující *editovatelný seznamový* řádek gridu, který umožňuje editaci obsahu buňek
 * @includeToDoc
 */
export interface IEditableListDataRowElement extends IListDataRowElement, IEditableDataRowElement {

}

export abstract class IEditableListDataRowElement {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance instanceof IListDataRowElement && instance instanceof IEditableDataRowElement;
    }
}

/**
 * Reprezentuje *element* představující kompaktní datový záznam *nezávislý na konečné vizuální podobě*
 * @includeToDoc
 */
export interface ICompactDataRowElement extends IDataRowElement {

    /**
     * Kolekce atributů pro zobrazení v kompaktním řádku.
     */
    visibleAttrs: ICompactDataGridAttrInfo;
}

export abstract class ICompactDataRowElement {
    private static _properties: (keyof ICompactDataRowElement)[] = ["visibleAttrs"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            instance instanceof IDataRowElement;
    }
}
